import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import { JsonLD } from '@/components/base/SchemaOrg';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = slug && slug !== 'all' ? categories.find((c) => c.slug === slug) : null;
  const allSlug = slug || 'all';

  const filteredArticles = allSlug === 'all'
    ? articles
    : articles.filter((a) => a.category === allSlug);

  const categoryName = currentCategory ? currentCategory.name : '全部文章';
  const pageTitle = currentCategory ? `${currentCategory.name} - ${t('category.title')}` : t('category.title');
  const pageDesc = currentCategory ? currentCategory.description : t('site.description');

  const breadcrumbItems = currentCategory
    ? [{ label: t('category.title'), href: '/category/all' }, { label: currentCategory.name }]
    : [{ label: t('category.title') }];

  return (
    <>
      <SEOMeta title={pageTitle} description={pageDesc} keywords={`${categoryName},大学校花,校园文化,校花评选`} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {categoryName}
              </h1>
              {currentCategory && (
                <p className="text-sm text-foreground-600">{currentCategory.description}</p>
              )}
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <Link
                to="/category/all"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${allSlug === 'all' ? 'bg-primary-500 text-white' : 'bg-background-50 text-foreground-700 border border-background-200 hover:bg-background-200'}`}
              >
                {t('category.all')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${allSlug === cat.slug ? 'bg-primary-500 text-white' : 'bg-background-50 text-foreground-700 border border-background-200 hover:bg-background-200'}`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-600">{t('category.noArticles')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}